import { useMemo, useEffect } from 'react';
import moment from 'moment';
import SessionReview from '../models/analytics/SessionReview';

interface UseProcessConversationalSuccessEventsOptions {
    crossOrgMode?: boolean;
    setConvertedCustomers?: (count: number) => void;
    setRepeatCustomers?: (count: number) => void;
    setUniqueEngagementCount?: (count: number) => void;
    setUpsellPercentage?: (percentage: number) => void;
}

interface ProcessingResults {
    reviews: SessionReview[];
    stats: {
        convertedCustomers: number;
        repeatCustomers: number;
        uniqueEngagementCount: number;
        upsellPercentage: number;
    };
}

/**
 * Hook that processes conversational success events into SessionReview objects.
 * Handles deduplication by sessionId and prioritizes events with categories.
 * 
 * Deduplication logic:
 * 1. Groups events by sessionId
 * 2. For duplicate sessionIds, prefers events with sessionCategory
 * 3. If both have/lack categories, uses most recent (by eventDate)
 * 4. Filters out events without sessionCategory
 */
export const useProcessConversationalSuccessEvents = (
    events: any[],
    sessionContainer: any,
    crossOrgSessionData: any[] = [],
    options: UseProcessConversationalSuccessEventsOptions = {}
): SessionReview[] => {
    const {
        crossOrgMode = false,
        setConvertedCustomers,
        setRepeatCustomers,
        setUniqueEngagementCount,
        setUpsellPercentage
    } = options;

    const processingResults = useMemo((): ProcessingResults => {
        const generativeSessionReviews: SessionReview[] = [];
        let upsellsAttempts = 0;
        let upsellCompletes = 0;

        if (!events || events.length === 0) {
            return {
                reviews: [],
                stats: {
                    convertedCustomers: 0,
                    repeatCustomers: 0,
                    uniqueEngagementCount: 0,
                    upsellPercentage: 0
                }
            };
        }

        // Create a map to track the best event for each sessionId
        const sessionEventMap = new Map<string, any>();
        for (const conversationalSuccessEvent of events) {
            let session = sessionContainer.sessions.find(s => s.sessionId === conversationalSuccessEvent.sessionId);
            if (!session) {
                if (crossOrgMode) {
                    session = crossOrgSessionData.find(s => s.sessionId === conversationalSuccessEvent.sessionId);
                }
                if (!session) {
                    continue;
                }
            }
            if (!conversationalSuccessEvent?.sessionId) {
                continue; // Skip events without sessionId
            }

            const sessionId = conversationalSuccessEvent.sessionId;
            const existingEvent = sessionEventMap.get(sessionId);

            if (!existingEvent) {
                // First event for this sessionId
                sessionEventMap.set(sessionId, conversationalSuccessEvent);
            } else {
                // Choose the better event based on priority:
                // 1. Prefer events with sessionCategory over those without
                // 2. If both have or both lack sessionCategory, prefer the one with a valid request id (integer) that is highest
                // 3. If both have or both lack sessionCategory and no request ids have integers, prefer the most recent (latest eventDate)

                const currentHasCategory = !!conversationalSuccessEvent.sessionCategory;
                const existingHasCategory = !!existingEvent.sessionCategory;

                let shouldReplace = false;

                if (currentHasCategory && !existingHasCategory) {
                    // Current has category, existing doesn't - replace
                    shouldReplace = true;
                } else if (!currentHasCategory && existingHasCategory) {
                    // Current doesn't have category, existing does - keep existing
                    shouldReplace = false;
                } else {
                    // Both have category or both don't have category - use most recent
                    const currentRequestId = `${conversationalSuccessEvent.requestId}`;
                    const existingRequestId = `${existingEvent.requestId}`;
                    const currentIsInteger = /^\d+$/.test(currentRequestId);
                    const existingIsInteger = /^\d+$/.test(existingRequestId);
                    if (currentIsInteger && !existingIsInteger) {
                        shouldReplace = true;
                    } else if (!currentIsInteger && existingIsInteger) {
                        shouldReplace = false;
                    } else if (currentIsInteger && existingIsInteger) {
                        // Both are integers, compare them as numbers
                        const currentRequestIdNum = parseInt(currentRequestId, 10);
                        const existingRequestIdNum = parseInt(existingRequestId, 10);
                        shouldReplace = currentRequestIdNum > existingRequestIdNum;
                    } else {
                        const currentDate = new Date(conversationalSuccessEvent.eventDate || 0);
                        const existingDate = new Date(existingEvent.eventDate || 0);
                        shouldReplace = currentDate > existingDate;
                    }
                }

                if (shouldReplace) {
                    sessionEventMap.set(sessionId, conversationalSuccessEvent);
                }
            }
        }

        // Convert the deduplicated events to SessionReviews, skipping those without categories
        for (const conversationalSuccessEvent of sessionEventMap.values()) {
            if (!conversationalSuccessEvent?.sessionCategory) {
                continue; // Skip events without sessionCategory
            }
            let session = null;
            if (crossOrgMode) {
                session = crossOrgSessionData.find(s => s.sessionId === conversationalSuccessEvent.sessionId);
            } else {
                session = sessionContainer.sessions.find(s => s.sessionId === conversationalSuccessEvent.sessionId);
            }
            generativeSessionReviews.push({
                sessionId: conversationalSuccessEvent.sessionId,
                summary: conversationalSuccessEvent.sessionSummary ?? "No summary",
                tags: conversationalSuccessEvent.sessionTags,
                category: conversationalSuccessEvent.sessionCategory,
                applicationId: conversationalSuccessEvent.applicationId,
                date: new Date(session?.startDate ?? session?.date ?? 0),
                userId: session?.from
            });
        }
        const groupedByUserId = generativeSessionReviews.reduce((acc, review) => {
            if (!acc[review.userId]) {
                acc[review.userId] = [];
            }
            acc[review.userId].push(review);
            return acc;
        }, {} as Record<string, SessionReview[]>);
        let convertedCustomers = 0;
        let repeatCustomers = 0;
        // foreach group order by date ascending, if there's a transfer tag before a placed tag add to converted customers
        Object.values(groupedByUserId).forEach(reviews => {
            reviews.sort((a, b) => a.date.getTime() - b.date.getTime());
            let hasTransfer = false;
            let hasConverted = false;
            let orderCount = 0;
            for (const review of reviews) {
                if (review.tags && review.tags.includes('TRANSFERRED')) {
                    hasTransfer = true;
                    continue;
                }
                if (review.tags.includes('ACTION_COMPLETED')) {
                    orderCount++;
                    if (hasTransfer && !hasConverted) {
                        convertedCustomers++;
                        hasConverted = true;
                    }
                }
            }
            if (orderCount > 1) {
                repeatCustomers++;
            }
        });

        // Sort by date ascending to process in chronological order
        generativeSessionReviews.sort((a, b) => a.date.getTime() - b.date.getTime());

        // Category priority for merging (higher number = higher priority)
        const categoryPriority = {
            'ACTION_ATTEMPTED': 5,
            'QUESTION_ANSWERED': 4,
            'OTHER': 3,
            'IMMEDIATE_TRANSFER': 2,
            'NO_INTERACTION': 1
        };
        // Combine sessions within 10 minutes that don't have ACTION_COMPLETED
        const combinedReviews: SessionReview[] = [];
        const processedIndices = new Set<number>();
        for (let i = 0; i < generativeSessionReviews.length; i++) {
            if (processedIndices.has(i)) continue;

            const currentReview = generativeSessionReviews[i];

            if (currentReview.tags?.includes('UPSELL_ATTEMPTED')) {
                upsellsAttempts++;
            }

            if (currentReview.tags?.includes('UPSELL_SUCCESSFUL')) {
                upsellCompletes++;
            }

            // Skip if this review already has ACTION_COMPLETED tag
            if (currentReview.tags && currentReview.tags.includes('ACTION_COMPLETED')) {
                combinedReviews.push(currentReview);
                processedIndices.add(i);
                continue;
            }

            // Find all reviews within 10 minutes after this one (rolling window)
            const relatedReviews = [currentReview];
            const relatedIndices = [i];
            let lastRelatedTime = currentReview.date.getTime(); // Track the time of the last related call
            for (let j = i + 1; j < generativeSessionReviews.length; j++) {
                if (processedIndices.has(j)) continue;

                const laterReview = generativeSessionReviews[j];
                const timeDifference = laterReview.date.getTime() - lastRelatedTime; // Compare to last related call, not first
                const tenMinutesInMs = 10 * 60 * 1000; // 10 minutes in milliseconds

                // Only combine calls if they have the same user ID and are within 10 minutes of the last related call
                if (timeDifference <= tenMinutesInMs && laterReview.userId === currentReview.userId) {
                    relatedReviews.push(laterReview);
                    relatedIndices.push(j);
                    lastRelatedTime = laterReview.date.getTime(); // Reset the timer - use this call as the new reference point
                } else if (timeDifference > tenMinutesInMs) {
                    // If we've gone beyond 10 minutes from the last related call, no need to check further reviews
                    break;
                }
            }

            // Mark all related indices as processed
            relatedIndices.forEach(index => processedIndices.add(index));

            if (relatedReviews.length === 1) {
                // No related reviews, add as-is
                combinedReviews.push({
                    ...currentReview,
                    mergedSessions: [{
                        sessionId: currentReview.sessionId,
                        startDate: currentReview.date ? currentReview.date.toISOString() : null
                    }]
                });
            } else {
                // Combine multiple related reviews

                // Determine the best category based on priority
                let bestCategory = relatedReviews[0].category;
                let highestPriority = categoryPriority[bestCategory] || 0;

                for (const review of relatedReviews) {
                    const priority = categoryPriority[review.category] || 0;
                    if (priority > highestPriority) {
                        highestPriority = priority;
                        bestCategory = review.category;
                    } else if (bestCategory === "OTHER" && review.category === "IMMEDIATE_TRANSFER") {
                        // If we end with an IMMEDIATE_TRANSFER, it takes precedence over OTHER
                        bestCategory = review.category;
                    }
                }

                // Combine tags from all sessions in the merge
                // For groupable tags (ACTION_COMPLETED, etc.), preserve all occurrences since each
                // session's tags already have the correct counts from the backend
                // For other tags, deduplicate to avoid redundancy
                const GROUPABLE_TAGS = [
                    'ACTION_COMPLETED',
                    'APPOINTMENT_RESCHEDULED',
                    'APPOINTMENT_CANCELLED',
                    'CHECK_NEXT_APPOINTMENT_COMPLETE'
                ];
                const allTags: string[] = [];
                const seenNonGroupableTags = new Set<string>();

                relatedReviews.forEach(review => {
                    if (review.tags) {
                        review.tags.forEach(tag => {
                            if (GROUPABLE_TAGS.includes(tag)) {
                                // Always add groupable tags - preserve all occurrences
                                // since each session's tags already have correct counts from backend
                                allTags.push(tag);
                            } else if (!seenNonGroupableTags.has(tag)) {
                                // Dedupe non-groupable tags across sessions
                                seenNonGroupableTags.add(tag);
                                allTags.push(tag);
                            }
                        });
                    }
                });

                // Combine summaries with call numbers, using custom messages for certain categories
                // Helper to get the formatted start time for a review
                const getStartTime = (review: SessionReview) => {
                    // Try to find the session for this review
                    let session = sessionContainer.sessions.find(s => s.sessionId === review.sessionId);
                    if (!session && crossOrgMode) {
                        session = crossOrgSessionData.find(s => s.sessionId === review.sessionId);
                    }
                    if (session && session?.startDate) {
                        // Use moment if available, else fallback to toLocaleTimeString
                        try {
                            return moment(session.startDate).format('h:mm a');
                        } catch {
                            return new Date(session.startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                        }
                    }
                    return '';
                };

                const combinedSummary = relatedReviews
                    .map((review, index) => {
                        let summary = review.summary && review.summary !== "No summary" ? review.summary : null;
                        if (!summary) {
                            if (review.category === "IMMEDIATE_TRANSFER") {
                                summary = "• Customer immediately requested to transfer.";
                            } else if (review.category === "NO_INTERACTION") {
                                summary = "• Customer didn't interact with the assistant.";
                            } else {
                                summary = "No summary available";
                            }
                        }
                        const startTime = getStartTime(review);
                        const callLabel = startTime ? `Call #${index + 1}  (${startTime})` : `Call #${index + 1}`;
                        return `${callLabel}\n${summary}`;
                    })
                    .join('\n\n')
                    .replace(/\n\s*\n/g, '\n'); // Remove empty lines

                // remove any tags that lowered start with "vfy_" from the combined tags
                let tags = [...allTags];
                if (!crossOrgMode) {
                    tags = tags.filter(tag => !tag?.toLowerCase().startsWith('vfy_'));
                }
                // Create combined review using the earliest session's data as base
                const combinedReview: SessionReview = {
                    sessionId: currentReview.sessionId, // Use the first session ID
                    summary: combinedSummary,
                    tags: tags,
                    category: bestCategory,
                    applicationId: currentReview.applicationId,
                    date: currentReview.date, // Use the earliest date
                    mergedSessions: relatedReviews.map(r => ({
                        sessionId: r.sessionId,
                        startDate: r.date ? r.date.toISOString() : null
                    }))
                };
                combinedReviews.push(combinedReview);
            }
        }

        return {
            reviews: combinedReviews,
            stats: {
                convertedCustomers,
                repeatCustomers,
                uniqueEngagementCount: combinedReviews.length,
                upsellPercentage: upsellsAttempts > 0 ? (upsellCompletes / upsellsAttempts) * 100 : 0
            }
        };
    }, [events, sessionContainer.sessions, crossOrgSessionData, crossOrgMode]);

    // Use useEffect to call state setters when results change
    useEffect(() => {
        setConvertedCustomers?.(processingResults.stats.convertedCustomers);
        setRepeatCustomers?.(processingResults.stats.repeatCustomers);
        setUniqueEngagementCount?.(processingResults.stats.uniqueEngagementCount);
        setUpsellPercentage?.(processingResults.stats.upsellPercentage);
    }, [processingResults.stats, setConvertedCustomers, setRepeatCustomers, setUniqueEngagementCount, setUpsellPercentage]);
    return processingResults.reviews;
};